<!--
@component

For displaying a list of items.

```svelte
<Pill value="Chocolate chip" onRemove={(value) => handleRemove(value)} />
```
-->

<script lang="ts">
import type { ClassValue } from 'svelte/elements';

import { Icon, type IconName } from './icon';
import { TooltipContainer, TooltipTarget, TooltipText } from './tooltip';

interface Props {
  /** The text in the pill. */
  value: string;
  /** Optional URL that the hyperlink points to. */
  href?: string;
  /** Optional target for linked URL. */
  target?: '_self' | '_blank' | '_parent' | '_top';
  /** Optional variant for visual distinction */
  variant?: 'dark' | 'base' | 'info' | 'warning';
  /** Whether or not the pill has the aria-readonly attribute. If readonly, there is not a button to onRemove the pill. */
  readonly?: boolean;
  /** Whether or not the pill is disabled. */
  disabled?: boolean;
  /** Whether or not the pill is removable. If removable, and not readonly, there is a button to onRemove the pill. */
  removable?: boolean;
  /** The icon shown in the button. */
  icon?: IconName;
  /** Tooltip for the icon in the pill */
  iconTooltip?: string;
  /** Additional CSS classes to pass to the pill icon. */
  iconCx?: ClassValue;
  /** Additional CSS classes to pass to the pill. */
  cx?: ClassValue;
  /** Callback function to handle removing the pill */
  onRemove?: (value: string) => void;
}

const {
  value,
  href = '',
  target = undefined,
  variant = undefined,
  readonly = false,
  disabled = false,
  removable = true,
  icon = undefined,
  iconTooltip = undefined,
  iconCx: extraIconClasses = '',
  cx: extraClasses = '',
  onRemove = undefined,
}: Props = $props();

const handleRemove = () => {
  if (disabled || readonly || !onRemove) {
    return;
  }

  onRemove(value);
};
</script>

<div
  data-testid={`pill-${value}`}
  class={[
    'flex w-fit max-w-full items-center gap-1 rounded text-xs whitespace-nowrap',
    {
      'h-6 gap-1.5 border pr-2 pl-1.5': icon,
      'border-medium bg-light': icon && variant !== 'dark',
      'border-dark bg-dark text-white': icon && variant === 'dark',
      'bg-medium gap-1  px-2 py-0.5': !icon,
      'border-info-medium !bg-info-light text-info-dark border':
        variant === 'info',
      'border-warning-medium !bg-warning-light text-warning-dark border':
        variant === 'warning',
      'bg-disabled-light text-disabled-dark': disabled || readonly,
      'cursor-not-allowed': disabled,
    },
    extraClasses,
  ]}
  aria-disabled={disabled ? true : undefined}
  aria-readonly={readonly ? true : undefined}
>
  {#if icon}
    <TooltipContainer let:tooltipID>
      <TooltipTarget>
        <Icon
          aria-describedby={tooltipID}
          {...iconTooltip
            ? { role: 'img', 'aria-hidden': false, 'aria-label': iconTooltip }
            : {}}
          name={icon}
          cx={extraIconClasses ||
            (variant === 'dark' ? 'text-white' : 'text-gray-6')}
          size="sm"
        />
      </TooltipTarget>
      {#if iconTooltip}
        <TooltipText>
          {iconTooltip}
        </TooltipText>
      {/if}
    </TooltipContainer>
  {/if}
  {#if href}
    <a
      {href}
      {target}
      class="truncate hover:underline">{value}</a
    >
  {:else}
    <span class="truncate">{value}</span>
  {/if}
  {#if !readonly && removable}
    <button
      type="button"
      aria-label="Remove {value}"
      onclick={handleRemove}
      class={[
        'text-gray-6 shrink-0',
        {
          'hover:text-default': !disabled,
          'cursor-not-allowed': disabled,
          'text-gray-200 hover:text-white': variant === 'dark',
          'text-info-dark hover:text-info-medium': variant === 'info',
          'text-warning-dark hover:text-warning-medium': variant === 'warning',
        },
      ]}
    >
      <Icon
        name="close"
        size="xs"
      />
    </button>
  {/if}
</div>
